/**
 * Acceleration pricing matrix
 * Maps from-tier + to-tier to credit cost and time saved
 */
const ACCELERATION_COSTS = {
    'free-starter': { credits: 500, hours: 6, saved: 3 },
    'free-pro': { credits: 1000, hours: 3, saved: 6 },
    'free-ultra': { credits: 1500, hours: 1, saved: 8 },
    'starter-pro': { credits: 500, hours: 3, saved: 3 },
    'starter-ultra': { credits: 1000, hours: 1, saved: 5 },
    'pro-ultra': { credits: 500, hours: 1, saved: 2 },
};

/**
 * Default delivery times by tier (in hours)
 */
export const DELIVERY_TIMES = {
    free: 9,
    starter: 6,
    pro: 3,
    ultra: 1
};

/**
 * Get available acceleration options for a user's current tier
 * @param {string} currentTier - User's subscription tier
 * @param {number} userCredits - User's available credits
 * @returns {Array<AccelerationOption>}
 */
export function getAccelerationOptions(currentTier, userCredits) {
    const normalized = currentTier.toLowerCase();
    const options = [];

    if (normalized === 'free') {
        options.push(
            createOption('starter', 'Starter Speed', 500, 6, 3, userCredits),
            createOption('pro', 'Pro Speed', 1000, 3, 6, userCredits),
            createOption('ultra', 'Ultra Speed', 1500, 1, 8, userCredits)
        );
    } else if (normalized === 'starter') {
        options.push(
            createOption('pro', 'Pro Speed', 500, 3, 3, userCredits),
            createOption('ultra', 'Ultra Speed', 1000, 1, 5, userCredits)
        );
    } else if (normalized === 'pro') {
        options.push(
            createOption('ultra', 'Ultra Speed', 500, 1, 2, userCredits)
        );
    }

    return options;
}

function createOption(level, label, credits, hours, saved, userCredits) {
    return {
        level,
        label,
        hours,
        credits,
        timeSaved: saved,
        canAfford: userCredits >= credits
    };
}

/**
 * Format time remaining until delivery
 * @param {string} deliveryTime - ISO 8601 timestamp
 * @returns {string} Human-readable time remaining
 */
export function formatTimeRemaining(deliveryTime) {
    const now = new Date();
    const delivery = new Date(deliveryTime);
    const diffMs = delivery - now;

    if (diffMs <= 0) return 'Overdue';

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
}

/**
 * Calculate estimated delivery time based on tier
 * @param {string} tier - Subscription tier
 * @param {Date} startTime - Report generation start time
 * @returns {Date} Estimated delivery time
 */
export function calculateDeliveryTime(tier, startTime = new Date()) {
    const normalized = tier.toLowerCase();
    const hours = DELIVERY_TIMES[normalized] || DELIVERY_TIMES.free;
    const deliveryTime = new Date(startTime);
    deliveryTime.setHours(deliveryTime.getHours() + hours);
    return deliveryTime;
}
